// src/pages/BackgroundsPage.js

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import { ReactComponent as FeatherIcon } from '../assets/icon-feather.svg'; // fallback voor Why/How

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

/**
 * Haal de eerste paragraaf-tekst uit een Strapi Blocks veld.
 * Vangt lege/afwijkende structuren af en geeft een string terug.
 */
function extractFirstParagraphFromBlocks(blocks) {
  if (!blocks || !Array.isArray(blocks)) return '';
  // Zoek eerste block met type 'paragraph' dat tekst bevat
  for (const block of blocks) {
    if (block?.type === 'paragraph' && Array.isArray(block.children)) {
      const text = block.children.map((c) => c?.text || '').join('');
      const cleaned = (text || '').replace(/\s+/g, ' ').trim();
      if (cleaned) return cleaned;
    }
  }
  // Als geen paragraaf, neem samenvoeging van alle text children van eerste block
  const first = blocks[0];
  if (first?.children && Array.isArray(first.children)) {
    const text = first.children.map((c) => c?.text || '').join('');
    return (text || '').replace(/\s+/g, ' ').trim();
  }
  return '';
}

function clampText(s, max = 140) {
  if (!s) return '';
  const trimmed = s.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max - 1).trimEnd() + '…';
}

function BackgroundsPage() {
  const [sourceData, setSourceData] = useState({ articles: [], whys: [], hows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // simpele filters (zoals in KennisbankPage maar nu 1 kolom)
  const [activeFilter, setActiveFilter] = useState(null); // 'artikel' | 'why' | 'how' | null

  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    const headers = { Authorization: `Bearer ${jwt}` };
    const fetchAllData = async () => {
      setLoading(true);
      setError('');
      try {
        const [artikelenRes, whyRes, howRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/artikels?sort=publication_date:desc&populate=featured_image`, { headers }),
          // BELANGRIJK: geen populate=description; alleen relations/media populaten.
          axios.get(`${STRAPI_URL}/api/whies?populate[steps][populate]=image`, { headers }),
          axios.get(`${STRAPI_URL}/api/hows?populate[steps][populate]=image`, { headers }),
        ]);
        setSourceData({
          articles: artikelenRes.data.data,
          whys: whyRes.data.data,
          hows: howRes.data.data,
        });
      } catch (err) {
        setError('De achtergronden laden lukte niet. Probeer het opnieuw.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const combined = useMemo(() => {
    // label items en sorteer op datum (articles: publication_date; why/how: createdAt)
    const articlesLabeled = sourceData.articles.map(a => ({
      ...a,
      __type: 'artikel',
      __date: a.publication_date || a.createdAt,
      __snippet: a.summary ? a.summary.trim() : '',
      __iconUrl: a.featured_image?.formats?.thumbnail?.url || a.featured_image?.url || null,
      __to: `/artikelen/${a.slug}`,
    }));

    const whysLabeled = sourceData.whys.map(w => {
      const snippet = clampText(extractFirstParagraphFromBlocks(w.description), 140);
      const stepImg = w.steps?.[0]?.image?.formats?.thumbnail?.url || w.steps?.[0]?.image?.url || null;
      return {
        ...w,
        __type: 'why',
        __date: w.createdAt,
        __snippet: snippet,
        __iconUrl: stepImg,
        __to: `/why/${w.slug}`,
      };
    });

    const howsLabeled = sourceData.hows.map(h => {
      const snippet = clampText(extractFirstParagraphFromBlocks(h.description), 140);
      const stepImg = h.steps?.[0]?.image?.formats?.thumbnail?.url || h.steps?.[0]?.image?.url || null;
      return {
        ...h,
        __type: 'how',
        __date: h.createdAt,
        __snippet: snippet,
        __iconUrl: stepImg,
        __to: `/how/${h.slug}`,
      };
    });

    return [...articlesLabeled, ...whysLabeled, ...howsLabeled]
      .sort((a, b) => new Date(b.__date) - new Date(a.__date));
  }, [sourceData]);

  const filtered = useMemo(() => {
    if (!activeFilter) return combined;
    return combined.filter(item => item.__type === activeFilter);
  }, [combined, activeFilter]);

  if (loading) return <p>Achtergronden worden geladen...</p>;
  if (error) return <p>{error}</p>;

  const renderCardIcon = (item) => {
    if (item.__iconUrl) {
      return <img src={item.__iconUrl} alt={item.title} className="card-icon" loading="lazy" />;
    }
    // Fallback icon (zelfde breedte dankzij .card-icon)
    return (
      <div className="card-icon">
        <FeatherIcon />
      </div>
    );
  };

  return (
    <div className="overview-page-grid">
      <main className="overview-main-content">
        <div className="overview-header">
          <h1>Achtergronden</h1>
        </div>

        <p>Artikelen en achtergrondinformatie (Waarom & Hoe). Gebruik de filters om te focussen.</p>

        <div className="filter-bar">
          <button
            className={`filter-btn ${activeFilter === null ? 'active' : ''}`}
            onClick={() => setActiveFilter(null)}
          >
            Alles
          </button>
          <button
            className={`filter-btn ${activeFilter === 'artikel' ? 'active' : ''}`}
            onClick={() => setActiveFilter(prev => prev === 'artikel' ? null : 'artikel')}
          >
            Artikelen
          </button>
          <button
            className={`filter-btn ${activeFilter === 'why' ? 'active' : ''}`}
            onClick={() => setActiveFilter(prev => prev === 'why' ? null : 'why')}
          >
            Waarom?
          </button>
          <button
            className={`filter-btn ${activeFilter === 'how' ? 'active' : ''}`}
            onClick={() => setActiveFilter(prev => prev === 'how' ? null : 'how')}
          >
            Hoe?
          </button>
        </div>

        <div className="doorloopje-list">
          {filtered.length > 0 ? filtered.map(item => (
            <Link to={item.__to} key={`${item.__type}-${item.id}`} className="doorloopje-card">
              {renderCardIcon(item)}
              <div className="card-text">
                <h3>{item.title}</h3>
                {/* Snippet tonen voor alle types */}
                {item.__snippet ? <p>{item.__snippet}</p> : null}
              </div>
              <div className="card-arrow">></div>
            </Link>
          )) : (
            <p>Geen items gevonden die aan de selectie voldoen.</p>
          )}
        </div>
      </main>

      <aside className="overview-sidebar">
        <h3>&nbsp;</h3>
      </aside>
    </div>
  );
}

export default BackgroundsPage;
